'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ArrowLeft, Calendar, Clock, User, Tag, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';

interface BlogPost {
  title: string;
  date: string;
  content: string;
  slug: string;
  excerpt: string;
  category: string;
  readTime: string;
  author: string;
  tags: string[];
}

interface BlogPostClientProps {
  post: BlogPost;
  relatedPosts: BlogPost[];
}

export default function BlogPostClient({ post, relatedPosts }: BlogPostClientProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Client-side structured data injection
  useEffect(() => {
    if (mounted && post) {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "author": {
          "@type": "Organization",
          "name": post.author,
          "url": "https://talksie.app"
        },
        "publisher": {
          "@type": "Organization", 
          "name": "Talksie",
          "logo": {
            "@type": "ImageObject",
            "url": "https://talksie.app/logo.png"
          }
        },
        "datePublished": new Date(post.date).toISOString(),
        "dateModified": new Date(post.date).toISOString(),
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://talksie.app/blog/${post.slug}`
        },
        "keywords": post.tags.join(", "),
        "articleSection": post.category,
        "url": `https://talksie.app/blog/${post.slug}`,
      };
      
      // Remove existing structured data script if present
      const existingScript = document.querySelector('script[data-blog-post-structured-data]');
      if (existingScript) {
        existingScript.remove();
      }
      
      // Add new structured data script
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-blog-post-structured-data', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [mounted, post]);

  // Share function
  const handleShare = async () => {
    if (!post) return;
    
    const shareData = {
      title: post.title,
      text: post.excerpt,
      url: `${window.location.origin}/blog/${post.slug}`
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy URL to clipboard
      try {
        await navigator.clipboard.writeText(shareData.url);
        alert('Link copied to clipboard!');
      } catch (error) {
        console.error('Failed to copy link');
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">      
      <div className="container mx-auto max-w-4xl px-4 py-16">
        {/* Header with navigation */}
        <div className="mb-12 flex items-center justify-between">
          <Link 
            href="/blog" 
            className="inline-flex items-center text-white hover:text-gray-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleShare}
              className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
              title="Share this post"
            >
              <Share2 className="w-4 h-4 mr-1" />
              Share
            </button>
            <Link href="/" className="text-2xl font-black tracking-tight text-white hover:text-gray-300 transition-colors">
              Talksie
            </Link>
          </div>
        </div>
        
        <article className="max-w-none">
          {/* Article Header */}
          <header className="mb-12">
            <Breadcrumbs
              className="mb-6"
              items={[
                { name: 'Home', path: '/' },
                { name: 'Blog', path: '/blog' },
                { name: post.title, path: `/blog/${post.slug}` },
              ]}
            />

            {/* Category Badge */}
            <div className="mb-6">
              <span className="px-4 py-2 bg-blue-900/30 text-blue-300 text-sm rounded-full border border-blue-600">
                {post.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white leading-tight">
              {post.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 text-gray-400 text-sm mb-6">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                <time dateTime={new Date(post.date).toISOString()}>
                  {post.date}
                </time>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span>{post.readTime}</span>
              </div>
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                <span>By {post.author}</span>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag) => (
                <span 
                  key={tag}
                  className="inline-flex items-center px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full border border-gray-700"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          </header>
          
          {/* Article Content */}
          <div className="max-w-none">
            <div 
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="mt-16 pt-8 border-t border-gray-800">
            <h2 className="text-2xl font-semibold mb-6 text-white">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <article key={relatedPost.slug} className="bg-gray-900 border border-gray-800 rounded-lg p-6 hover:border-gray-700 transition-colors">
                  <div className="mb-3">
                    <span className="px-2 py-1 bg-blue-900/30 text-blue-300 text-xs rounded-full border border-blue-600">
                      {relatedPost.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                    <Link 
                      href={`/blog/${relatedPost.slug}`}
                      className="hover:text-blue-400 transition-colors"
                    >
                      {relatedPost.title}
                    </Link>
                  </h3>
                  <p className="text-gray-300 text-sm line-clamp-3 mb-3">
                    {relatedPost.excerpt}
                  </p>
                  <div className="flex items-center text-gray-500 text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    <span>{relatedPost.readTime}</span>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="mt-16 p-8 bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-800 rounded-lg text-center">
          <h2 className="text-2xl font-semibold mb-4 text-white">Ready to Connect with Professional Interpreters?</h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Join thousands of businesses who trust Talksie for their international communication needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              href="/find-interpreter"
              className="inline-flex items-center px-6 py-3 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Find an Interpreter
            </Link>
            <Link 
              href="/contact"
              className="inline-flex items-center px-6 py-3 border border-gray-600 text-gray-300 font-semibold rounded-lg hover:bg-gray-800 hover:text-white transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </section>
        
        {/* Navigation Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <Link 
              href="/blog" 
              className="inline-flex items-center text-white hover:text-gray-300 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to all posts
            </Link>
            <div className="flex space-x-4">
              <Link href="/about" className="text-gray-400 hover:text-white transition-colors text-sm">
                About
              </Link>
              <Link href="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">
                Contact
              </Link>
              <Link href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors text-sm">
                Privacy
              </Link>
            </div>
          </div>
        </footer>
      </div>

      {/* Enhanced CSS for blog content styling */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .blog-content h2 {
            color: #ffffff;
            font-size: 1.875rem;
            font-weight: 600;
            margin-top: 2.5rem;
            margin-bottom: 1.25rem;
            line-height: 1.2;
            border-bottom: 1px solid #374151;
            padding-bottom: 0.5rem;
          }
          
          .blog-content h3 {
            color: #ffffff;
            font-size: 1.5rem;
            font-weight: 600;
            margin-top: 2rem;
            margin-bottom: 1rem;
          }
          
          .blog-content p {
            color: #d1d5db;
            line-height: 1.8;
            margin-bottom: 1.75rem;
            font-size: 1.1rem;
          }
          
          .blog-content strong {
            color: #ffffff;
            font-weight: 600;
          }
          
          .blog-content ul, .blog-content ol {
            color: #d1d5db;
            margin-bottom: 1.75rem;
            padding-left: 1.75rem;
          }
          
          .blog-content li {
            margin-bottom: 0.75rem;
            line-height: 1.8;
            font-size: 1.05rem;
          }
          
          .blog-content li::marker {
            color: #9ca3af;
          }
          
          .blog-content ul li {
            position: relative;
          }
          
          .blog-content ul li::before {
            content: '';
            position: absolute;
            left: -1.25rem;
            top: 0.7rem;
            width: 6px;
            height: 6px;
            background-color: #60a5fa;
            border-radius: 50%;
          }
          
          .blog-content ol li {
            counter-increment: step-counter;
          }
          
          .blog-content ol {
            counter-reset: step-counter;
          }
          
          .blog-content blockquote {
            border-left: 4px solid #3b82f6;
            background-color: #1f2937;
            padding: 1.5rem;
            margin: 2rem 0;
            font-style: italic;
            color: #e5e7eb;
            border-radius: 0.5rem;
          }
          
          .blog-content code {
            background-color: #374151;
            color: #f3f4f6;
            padding: 0.25rem 0.5rem;
            border-radius: 0.375rem;
            font-size: 0.875em;
            font-family: 'Monaco', 'Menlo', monospace;
          }
          
          .blog-content pre {
            background-color: #1f2937;
            color: #f3f4f6;
            padding: 1.5rem;
            border-radius: 0.75rem;
            overflow-x: auto;
            margin: 2rem 0;
            border: 1px solid #374151;
          }

          .blog-content pre code {
            background: none;
            padding: 0;
            border-radius: 0;
          }

          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `
      }} />
    </div>
  );
}
